import fs from "node:fs";
import os from "node:os";
import path from "node:path";
import { z } from "incur";
import { Profile } from "./types";

/**
 * Plaintext metadata store at `~/.config/whop/config.json`.
 *
 * Holds the profile list and the active profile — never secrets, which live in
 * the OS keychain or credentials file (see `secret-store.ts`). Reads are
 * corruption-tolerant: a missing
 * or invalid file resolves to an empty config rather than throwing, so the CLI
 * is never bricked by a bad file. Valid profiles are kept even if another
 * profile entry is malformed.
 */

export const Config = z.object({
	version: z.literal(1).default(1),
	activeProfile: z.string().optional(),
	profiles: z.array(Profile).default([]),
});
export type Config = z.infer<typeof Config>;

/** Resolves the config directory, honoring `WHOP_CONFIG_DIR` and `XDG_CONFIG_HOME` overrides. */
export function configDir(env: NodeJS.ProcessEnv = process.env): string {
	const override = env.WHOP_CONFIG_DIR?.trim();
	if (override) return override;
	const xdg = env.XDG_CONFIG_HOME?.trim();
	const base = xdg ? xdg : path.join(os.homedir(), ".config");
	return path.join(base, "whop");
}

export function configPath(env: NodeJS.ProcessEnv = process.env): string {
	return path.join(configDir(env), "config.json");
}

export function readConfig(env: NodeJS.ProcessEnv = process.env): Config {
	let raw: string;
	try {
		raw = fs.readFileSync(configPath(env), "utf8");
	} catch {
		return Config.parse({});
	}
	let config: Config;
	try {
		config = parseConfig(JSON.parse(raw));
	} catch {
		return Config.parse({});
	}

	return config;
}

function parseConfig(value: unknown): Config {
	const parsed = Config.safeParse(value);
	if (parsed.success) return parsed.data;
	if (!value || typeof value !== "object" || Array.isArray(value)) {
		return Config.parse({});
	}

	const raw = value as { activeProfile?: unknown; profiles?: unknown };
	const profiles: Profile[] = [];
	if (Array.isArray(raw.profiles)) {
		for (const profile of raw.profiles) {
			const parsed = Profile.safeParse(profile);
			if (parsed.success) profiles.push(parsed.data);
		}
	}
	const activeProfile =
		typeof raw.activeProfile === "string" &&
		profiles.some((profile) => profile.name === raw.activeProfile)
			? raw.activeProfile
			: undefined;

	return Config.parse({ activeProfile, profiles });
}

export function writeConfig(
	config: Config,
	env: NodeJS.ProcessEnv = process.env,
): void {
	const dir = configDir(env);
	fs.mkdirSync(dir, { recursive: true, mode: 0o700 });
	const file = configPath(env);
	const tmp = `${file}.${process.pid}.tmp`;
	fs.writeFileSync(tmp, `${JSON.stringify(config, null, 2)}\n`, {
		mode: 0o600,
	});
	fs.renameSync(tmp, file);
	try {
		fs.chmodSync(file, 0o600);
	} catch {
		// best effort — chmod is a no-op on some platforms (e.g. Windows)
	}
}
